import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { getAreaBySlug, LONDON_AREAS, type LondonArea } from "@/lib/london-areas";
import { getServiceBySlug, SERVICES, serviceFaqs, type ServiceDef } from "@/lib/services-catalog";
import { breadcrumb, faqPage } from "@/lib/seo-jsonld";
import { ArrowRight, Check, MapPin, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/london/$area/$service")({
  loader: ({ params }) => {
    const area = getAreaBySlug(params.area);
    const service = getServiceBySlug(params.service);
    if (!area || !service) throw notFound();
    return { area, service };
  },
  head: ({ loaderData, params }) => {
    const area = loaderData?.area;
    const service = loaderData?.service;
    const aName = area?.name ?? params.area;
    const sName = service?.name ?? params.service;
    const title = `${sName} in ${aName} from £${service?.rateLow ?? 25}/hr — Peopels.com`;
    const description = service && area
      ? `${service.summary} Vetted local Helpers across ${area.name} (${area.postcodes.join(", ")}). Pay in escrow — released only when the job is done.`
      : `${sName} in ${aName} with Peopels.com.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `/london/${params.area}/${params.service}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `/london/${params.area}/${params.service}` }],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Page not found</h1>
        <Link to="/london" className="mt-4 inline-block text-primary underline">Browse London areas</Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
        <Link to="/london" className="mt-4 inline-block text-primary underline">Back to London</Link>
      </main>
      <Footer />
    </div>
  ),
  component: ServiceAreaPage,
});

function ServiceAreaPage() {
  const { area, service } = Route.useLoaderData() as { area: LondonArea; service: ServiceDef };
  const neighbours = area.neighbours
    .map((slug: string) => LONDON_AREAS.find((a) => a.slug === slug))
    .filter((a): a is LondonArea => !!a);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);
  const faqs = serviceFaqs(service.slug, area.name);
  const url = `/london/${area.slug}/${service.slug}`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Greater London", to: "/london" },
            { label: area.name, to: `/london/${area.slug}` },
            { label: service.name },
          ]}
        />

        <header className="mb-8">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">
            <MapPin className="h-3.5 w-3.5" /> {area.name} · {area.postcodes.join(" · ")}
          </p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-5xl">
            {service.name} in {area.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
            {service.summary} {area.intro}
          </p>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {area.housing}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1"><ShieldCheck className="h-3 w-3 text-primary" /> ID-verified</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1">Stripe escrow</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1">Pay when done</span>
          </div>
          <Link
            to="/post-job"
            search={{ category: service.slug }}
            className="mt-5 inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Get matched in {area.name}
          </Link>
        </header>

        <section aria-labelledby="includes-h" className="mb-10 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 id="includes-h" className="font-display text-lg font-semibold">What's included</h2>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              {service.includes.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Typical {service.name.toLowerCase()} prices in {area.name}</h2>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { label: "Low", v: service.rateLow },
                { label: "Expected", v: service.rateMid, primary: true },
                { label: "Premium", v: service.rateHigh },
              ].map((t) => (
                <div key={t.label} className={`rounded-xl border px-3 py-2.5 text-center ${t.primary ? "border-primary/30 bg-primary/5" : "border-border bg-background"}`}>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t.label}</p>
                  <p className={`mt-0.5 text-base font-semibold tabular-nums ${t.primary ? "text-primary" : "text-foreground"}`}>£{t.v}/hr</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Estimate only — not a guaranteed quote. Average {service.name.toLowerCase()} job in {area.name} takes ~{Math.round(service.avgDurationMinutes / 60 * 10) / 10} hr.
            </p>
          </div>
        </section>

        <section aria-labelledby="faq-h" className="mb-10">
          <h2 id="faq-h" className="font-display text-2xl font-semibold">FAQs — {service.name} in {area.name}</h2>
          <dl className="mt-4 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:shadow-card">
                <summary className="cursor-pointer list-none text-sm font-medium">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </dl>
        </section>

        <section aria-labelledby="other-h" className="mb-10">
          <h2 id="other-h" className="font-display text-xl font-semibold">Other services in {area.name}</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-3">
            {otherServices.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/london/$area/$service"
                  params={{ area: area.slug, service: s.slug }}
                  className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5 text-sm transition hover:border-primary/40"
                >
                  <span>{s.name} in {area.name}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="near-h" className="mb-10">
          <h2 id="near-h" className="font-display text-xl font-semibold">{service.name} in nearby areas</h2>
          <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {neighbours.map((n: LondonArea) => (
              <li key={n.slug}>
                <Link
                  to="/london/$area/$service"
                  params={{ area: n.slug, service: service.slug }}
                  className="block rounded-xl border border-border bg-card px-3 py-2.5 text-sm transition hover:border-primary/40"
                >
                  <span className="block font-medium">{service.shortName} in {n.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{n.postcodes.join(" · ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-6 text-center shadow-card">
          <h2 className="font-display text-xl font-semibold">Book a {service.shortName} Helper in {area.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Free to post, and you'll see estimates before you pay.</p>
          <Link
            to="/post-job"
            search={{ category: service.slug }}
            className="mt-4 inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Post your {service.name.toLowerCase()} job
          </Link>
        </section>

        {/* JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              breadcrumb([
                { name: "Home", path: "/" },
                { name: "Greater London", path: "/london" },
                { name: area.name, path: `/london/${area.slug}` },
                { name: service.name, path: url },
              ]),
            ),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPage(faqs)) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              name: `${service.name} in ${area.name}`,
              serviceType: service.name,
              provider: { "@type": "Organization", name: "Peopels.com", url: "/" },
              areaServed: {
                "@type": "Place",
                name: `${area.name}, London`,
                address: { "@type": "PostalAddress", addressLocality: area.name, addressRegion: "London", postalCode: area.postcodes[0], addressCountry: "GB" },
              },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "GBP",
                lowPrice: service.rateLow,
                highPrice: service.rateHigh,
                priceSpecification: { "@type": "UnitPriceSpecification", price: service.rateMid, priceCurrency: "GBP", unitText: "HUR" },
              },
              url,
              description: `${service.summary} Vetted local Helpers across ${area.name} (${area.postcodes.join(", ")}).`,
            }),
          }}
        />
      </main>
      <Footer />
    </div>
  );
}
